import { useState, useCallback } from "react";
import {
  loadNotifications,
  saveNotification,
  deleteNotification,
  toggleNotification,
  stageNotificationsToStaging,
  NOTIFY_BANNER_ID,
  type SiteNotification,
  type SiteBanner,
  type SitePopup,
  type BannerStyle,
  type BannerPosition,
  type BannerThickness,
  type PopupTrigger,
} from "@/utils/popupBannerStore";
import { commitAllStagedChanges } from "@/hooks/useAdminStorage";
import { saveSiteContentToSupabase } from "@/hooks/useSiteContent";

const ADMIN_PASSWORD = "makersalley2024";

const STYLE_OPTIONS: { value: BannerStyle; label: string; preview: string }[] = [
  { value: "info",    label: "Info (Dark)",   preview: "bg-gray-900 text-white" },
  { value: "success", label: "Success (Green)", preview: "bg-green-500 text-white" },
  { value: "warning", label: "Warning (Amber)", preview: "bg-amber-400 text-amber-900" },
  { value: "promo",   label: "Promo (Pink)",  preview: "bg-pink-500 text-white" },
];

const TRIGGER_OPTIONS: { value: PopupTrigger; label: string; desc: string }[] = [
  { value: "on-load",    label: "On Page Load",   desc: "Shows immediately when the page opens" },
  { value: "after-delay", label: "After Delay",   desc: "Shows after N seconds on the page" },
  { value: "on-exit",    label: "On Exit Intent", desc: "Shows when the cursor moves toward closing the tab" },
];

function newBanner(): SiteBanner {
  return {
    id: `banner-${Date.now()}`,
    type: "banner",
    enabled: false,
    text: "Welcome to Makers Alley Co — coming soon to Medford, OR!",
    subText: "",
    style: "promo",
    dismissible: true,
    ctaLabel: "",
    ctaUrl: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function newPopup(): SitePopup {
  return {
    id: `popup-${Date.now()}`,
    type: "popup",
    enabled: false,
    title: "Stay in the Loop!",
    body: "Be the first to know when Makers Alley Co opens. Sign up for updates and get early access.",
    style: "promo",
    trigger: "after-delay",
    delaySeconds: 5,
    ctaLabel: "Get Notified",
    ctaUrl: "#application",
    showOnce: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// ─── Banner Editor ────────────────────────────────────────────────────────────

function BannerEditor({
  banner,
  onSave,
  onCancel,
  isDark,
}: {
  banner: SiteBanner;
  onSave: (b: SiteBanner) => void;
  onCancel: () => void;
  isDark: boolean;
}) {
  const [draft, setDraft] = useState<SiteBanner>({ ...banner });

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";

  const previewColors = STYLE_OPTIONS.find((s) => s.value === draft.style)?.preview ?? "bg-gray-900 text-white";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <h3 className={`font-bold text-base ${headingColor}`}>
          {banner.id.startsWith("banner-") && banner.text === "Welcome to Makers Alley Co — coming soon to Medford, OR!" ? "New Banner" : "Edit Banner"}
        </h3>
      </div>

      {/* Live preview */}
      <div className={`rounded-2xl overflow-hidden border ${cardBg}`}>
        <div className={`px-5 py-3 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <span className={`text-xs font-bold uppercase tracking-wide ${subColor}`}>Live Preview</span>
        </div>
        <div className={`${previewColors} py-2.5 px-4 flex items-center justify-center gap-3 text-sm font-medium relative`}>
          <span className="text-center">
            {draft.text || "Your banner text here"}
            {draft.subText && <span className="ml-2 opacity-80 text-xs">{draft.subText}</span>}
          </span>
          {draft.ctaLabel && (
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 whitespace-nowrap">
              {draft.ctaLabel}
            </span>
          )}
          {draft.dismissible && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-white/20 opacity-70">
              <i className="ri-close-line text-sm"></i>
            </span>
          )}
        </div>
      </div>

      <div className={`rounded-2xl border p-5 space-y-4 ${cardBg}`}>
        <div>
          <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Main Text *</label>
          <input type="text" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} placeholder="Your announcement text..." className={inputCls} />
        </div>
        <div>
          <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Sub Text (optional)</label>
          <input type="text" value={draft.subText ?? ""} onChange={(e) => setDraft({ ...draft, subText: e.target.value })} placeholder="Smaller supporting text..." className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Button Label (optional)</label>
            <input type="text" value={draft.ctaLabel ?? ""} onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })} placeholder="e.g. Learn More" className={inputCls} />
          </div>
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Button URL (optional)</label>
            <input type="text" value={draft.ctaUrl ?? ""} onChange={(e) => setDraft({ ...draft, ctaUrl: e.target.value })} placeholder="e.g. /artists or https://..." className={inputCls} />
          </div>
        </div>

        {/* Style */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Color Style</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {STYLE_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => setDraft({ ...draft, style: s.value })}
                className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${draft.style === s.value ? "border-pink-500" : isDark ? "border-gray-600" : "border-gray-200"}`}
              >
                <div className={`${s.preview} py-2 text-xs font-bold text-center`}>{s.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Position */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Position</label>
          <div className="flex gap-2">
            {(["top", "bottom"] as BannerPosition[]).map((pos) => (
              <button
                key={pos}
                onClick={() => setDraft({ ...draft, position: pos })}
                className={`flex-1 text-xs font-bold py-2 rounded-xl border-2 cursor-pointer transition-all capitalize ${
                  (draft.position ?? "top") === pos
                    ? "border-pink-500 bg-pink-50 text-pink-700"
                    : isDark ? "border-gray-600 text-gray-400" : "border-gray-200 text-gray-500"
                }`}
              >
                <i className={`${pos === "top" ? "ri-arrow-up-line" : "ri-arrow-down-line"} mr-1`}></i>
                {pos === "top" ? "Top of page" : "Bottom of page"}
              </button>
            ))}
          </div>
        </div>

        {/* Thickness */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Thickness</label>
          <div className="flex gap-2">
            {(["slim", "normal", "tall"] as BannerThickness[]).map((t) => (
              <button
                key={t}
                onClick={() => setDraft({ ...draft, thickness: t })}
                className={`flex-1 text-xs font-bold py-2 rounded-xl border-2 cursor-pointer transition-all capitalize ${
                  (draft.thickness ?? "normal") === t
                    ? "border-pink-500 bg-pink-50 text-pink-700"
                    : isDark ? "border-gray-600 text-gray-400" : "border-gray-200 text-gray-500"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.dismissible}
              onChange={(e) => setDraft({ ...draft, dismissible: e.target.checked })}
              className="accent-pink-500"
            />
            <span className={`text-sm ${headingColor}`}>Dismissible (show × button)</span>
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => onSave({ ...draft, updatedAt: new Date().toISOString() })}
          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-save-line mr-1.5"></i>Save Banner
        </button>
        <button
          onClick={onCancel}
          className={`px-6 border font-semibold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300" : "border-gray-200 text-gray-600"}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Popup Editor ─────────────────────────────────────────────────────────────

function PopupEditor({
  popup,
  onSave,
  onCancel,
  isDark,
}: {
  popup: SitePopup;
  onSave: (p: SitePopup) => void;
  onCancel: () => void;
  isDark: boolean;
}) {
  const [draft, setDraft] = useState<SitePopup>({ ...popup });

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";

  const accentBorder = {
    info: "border-gray-400",
    success: "border-green-400",
    warning: "border-amber-400",
    promo: "border-pink-400",
  }[draft.style];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        <h3 className={`font-bold text-base ${headingColor}`}>Edit Popup</h3>
      </div>

      {/* Live preview */}
      <div className={`rounded-2xl overflow-hidden border ${cardBg}`}>
        <div className={`px-5 py-3 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <span className={`text-xs font-bold uppercase tracking-wide ${subColor}`}>Live Preview</span>
        </div>
        <div className="p-5">
          <div className={`w-full max-w-sm mx-auto bg-white rounded-3xl border-t-4 ${accentBorder} overflow-hidden`}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-base font-bold text-gray-900 pr-4">{draft.title || "Popup Title"}</h4>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 flex-shrink-0">
                  <i className="ri-close-line text-sm"></i>
                </div>
              </div>
              <p className="text-gray-600 text-xs leading-relaxed mb-4">{draft.body || "Popup body text..."}</p>
              <div className="flex gap-2">
                {draft.ctaLabel && (
                  <div className={`flex-1 text-center text-xs font-bold py-2 rounded-full text-white ${
                    { info: "bg-gray-800", success: "bg-green-500", warning: "bg-amber-500", promo: "bg-pink-500" }[draft.style]
                  }`}>
                    {draft.ctaLabel}
                  </div>
                )}
                <div className="flex-1 text-center text-xs font-semibold py-2 rounded-full border border-gray-200 text-gray-500">Close</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border p-5 space-y-4 ${cardBg}`}>
        <div>
          <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Popup Title *</label>
          <input type="text" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Stay in the Loop!" className={inputCls} />
        </div>
        <div>
          <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Body Text *</label>
          <textarea
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            placeholder="What do you want to say to visitors?"
            rows={3}
            className={`${inputCls} resize-none`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Button Label (optional)</label>
            <input type="text" value={draft.ctaLabel ?? ""} onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })} placeholder="e.g. Get Notified" className={inputCls} />
          </div>
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Button URL (optional)</label>
            <input type="text" value={draft.ctaUrl ?? ""} onChange={(e) => setDraft({ ...draft, ctaUrl: e.target.value })} placeholder="e.g. #application" className={inputCls} />
          </div>
        </div>

        {/* Style */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Color Accent</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {STYLE_OPTIONS.map((s) => (
              <button
                key={s.value}
                onClick={() => setDraft({ ...draft, style: s.value })}
                className={`rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${draft.style === s.value ? "border-pink-500" : isDark ? "border-gray-600" : "border-gray-200"}`}
              >
                <div className={`${s.preview} py-2 text-xs font-bold text-center`}>{s.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Trigger */}
        <div>
          <label className={`text-xs font-semibold mb-2 block ${subColor}`}>When to Show</label>
          <div className="space-y-2">
            {TRIGGER_OPTIONS.map((t) => (
              <label key={t.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                draft.trigger === t.value
                  ? isDark ? "border-pink-500 bg-pink-900/20" : "border-pink-400 bg-pink-50"
                  : isDark ? "border-gray-600 hover:border-gray-500" : "border-gray-200 hover:border-gray-300"
              }`}>
                <input
                  type="radio"
                  name="trigger"
                  value={t.value}
                  checked={draft.trigger === t.value}
                  onChange={() => setDraft({ ...draft, trigger: t.value })}
                  className="accent-pink-500 mt-0.5"
                />
                <div>
                  <div className={`text-xs font-bold ${headingColor}`}>{t.label}</div>
                  <div className={`text-xs ${subColor}`}>{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {draft.trigger === "after-delay" && (
          <div>
            <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Delay (seconds)</label>
            <input
              type="number"
              min={1}
              max={60}
              value={draft.delaySeconds}
              onChange={(e) => setDraft({ ...draft, delaySeconds: Number(e.target.value) })}
              className={`${inputCls} w-32`}
            />
          </div>
        )}

        {/* Options */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.showOnce}
              onChange={(e) => setDraft({ ...draft, showOnce: e.target.checked })}
              className="accent-pink-500"
            />
            <span className={`text-sm ${headingColor}`}>Show only once per visitor</span>
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => onSave({ ...draft, updatedAt: new Date().toISOString() })}
          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-save-line mr-1.5"></i>Save Popup
        </button>
        <button
          onClick={onCancel}
          className={`px-6 border font-semibold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300" : "border-gray-200 text-gray-600"}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export default function PopupsBannersTab({ isDark }: { isDark: boolean }) {
  // Use a draft state — changes only commit when user clicks Save
  const [draftItems, setDraftItems] = useState<SiteNotification[]>(() => loadNotifications());
  const [hasChanges, setHasChanges] = useState(false);
  const [editing, setEditing] = useState<SiteNotification | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const rowHover = isDark ? "hover:bg-gray-700/30" : "hover:bg-gray-50";

  const stageDraft = useCallback((items: SiteNotification[]) => {
    stageNotificationsToStaging(items);
    setHasChanges(true);
  }, []);

  const commit = useCallback(async () => {
    setSaveState("saving");
    // Write all draft items to localStorage
    draftItems.forEach((item) => {
      saveNotification(item);
    });
    // Stage for Supabase sync
    stageNotificationsToStaging(draftItems);
    // Commit staging to localStorage and sync to cloud
    commitAllStagedChanges();
    const result = await saveSiteContentToSupabase({}, ADMIN_PASSWORD);
    if (result.success) {
      setSaveState("saved");
      setHasChanges(false);
      setTimeout(() => setSaveState("idle"), 2000);
    } else {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 3000);
    }
  }, [draftItems]);

  const discard = useCallback(() => {
    setDraftItems(loadNotifications());
    setHasChanges(false);
    setEditing(null);
    setDeleteConfirm(null);
  }, []);

  const handleSave = (item: SiteNotification) => {
    const idx = draftItems.findIndex((n) => n.id === item.id);
    let next: SiteNotification[];
    if (idx >= 0) {
      next = draftItems.map((n, i) => i === idx ? item : n);
    } else {
      next = [...draftItems, item];
    }
    setDraftItems(next);
    stageDraft(next);
    setEditing(null);
  };

  const handleToggle = (id: string) => {
    const next = draftItems.map((n) =>
      n.id === id ? { ...n, enabled: !n.enabled, updatedAt: new Date().toISOString() } : n
    );
    setDraftItems(next);
    stageDraft(next);
  };

  const handleDelete = (id: string) => {
    const next = draftItems.filter((n) => n.id !== id);
    setDraftItems(next);
    stageDraft(next);
    setDeleteConfirm(null);
  };

  if (editing) {
    if (editing.type === "banner") {
      return <BannerEditor banner={editing as SiteBanner} onSave={handleSave} onCancel={() => setEditing(null)} isDark={isDark} />;
    }
    return <PopupEditor popup={editing as SitePopup} onSave={handleSave} onCancel={() => setEditing(null)} isDark={isDark} />;
  }

  const banners = draftItems.filter((i) => i.type === "banner") as SiteBanner[];
  const popups = draftItems.filter((i) => i.type === "popup") as SitePopup[];

  const styleLabel = (style: BannerStyle) => STYLE_OPTIONS.find((s) => s.value === style)?.label ?? style;
  const triggerLabel = (t: PopupTrigger) => TRIGGER_OPTIONS.find((o) => o.value === t)?.label ?? t;

  const renderRow = (item: SiteNotification) => {
    const isBanner = item.type === "banner";
    const banner = item as SiteBanner;
    const popup = item as SitePopup;

    return (
      <div key={item.id} className={`flex items-center gap-4 px-5 py-4 transition-colors ${rowHover}`}>
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
          <i className={`${isBanner ? "ri-megaphone-line" : "ri-window-line"} text-pink-400 text-base`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-bold text-sm ${headingColor}`}>
            {isBanner ? (banner.text.slice(0, 50) + (banner.text.length > 50 ? "…" : "")) : popup.title}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
              {isBanner ? "Banner" : "Popup"}
            </span>
            {isBanner && item.id === NOTIFY_BANNER_ID && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-pink-100 text-pink-600">
                <i className="ri-mail-line mr-0.5"></i>Email Capture
              </span>
            )}
            {isBanner && banner.position === "bottom" && (
              <span className={`text-xs ${subColor}`}>↓ Bottom</span>
            )}
            <span className={`text-xs ${subColor}`}>{styleLabel(item.style)}</span>
            {!isBanner && <span className={`text-xs ${subColor}`}>· {triggerLabel(popup.trigger)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Live toggle */}
          <button
            onClick={() => handleToggle(item.id)}
            className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${item.enabled ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
            title={item.enabled ? "Disable" : "Enable"}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${item.enabled ? "translate-x-5" : "translate-x-0.5"}`}></span>
          </button>
          <span className={`text-xs font-bold w-12 ${item.enabled ? "text-green-500" : subColor}`}>
            {item.enabled ? "Live" : "Off"}
          </span>
          {/* Edit */}
          <button
            onClick={() => setEditing(item)}
            className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
            title="Edit"
          >
            <i className="ri-edit-line text-sm"></i>
          </button>
          {/* Delete */}
          {deleteConfirm === item.id ? (
            <div className="flex items-center gap-1">
              <button onClick={() => handleDelete(item.id)} className="text-xs font-bold text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap">Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className={`text-xs cursor-pointer whitespace-nowrap ${subColor}`}>Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirm(item.id)}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors text-red-400 hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Save/Discard bar */}
      {hasChanges && (
        <div className={`rounded-2xl border p-4 flex items-center justify-between ${isDark ? "bg-amber-900/20 border-amber-800/40" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
              <i className="ri-alert-line text-amber-600 text-sm"></i>
            </div>
            <div>
              <div className={`font-bold text-sm ${headingColor}`}>Unsaved Changes</div>
              <div className={`text-xs ${subColor}`}>You have unsaved changes to banners and popups. Save to make them live, or discard to revert.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={discard}
              className={`text-xs font-semibold px-3 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className="ri-close-line mr-1"></i>Discard
            </button>
            <button
              onClick={commit}
              className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-save-line mr-1"></i>Save Changes
            </button>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            Popups &amp; Banners
          </h2>
          <p className={`text-sm ${subColor}`}>
            Create announcement banners (strips at the top of every page) and popup modals. Toggle them on or off instantly.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(newBanner())}
            className={`flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "bg-gray-700 text-gray-200 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            <i className="ri-megaphone-line"></i> New Banner
          </button>
          <button
            onClick={() => setEditing(newPopup())}
            className="flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors bg-pink-500 hover:bg-pink-600 text-white"
          >
            <i className="ri-window-line"></i> New Popup
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className={`rounded-2xl border p-5 ${cardBg}`}>
        <h3 className={`font-bold text-sm mb-3 ${headingColor}`}>
          <i className="ri-information-line text-pink-400 mr-1.5"></i>
          How It Works
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: "ri-megaphone-line", title: "Announcement Banners", desc: "A colored strip at the very top of every page. Great for sales, announcements, or coming-soon notices. Can be dismissible." },
            { icon: "ri-window-line", title: "Popup Modals", desc: "A centered modal that appears on load, after a delay, or on exit intent. Perfect for newsletter signups or special offers." },
            { icon: "ri-toggle-line", title: "Toggle On/Off", desc: "Flip the switch to make any banner or popup live instantly — no page reload needed. Visitors see changes right away." },
          ].map((item) => (
            <div key={item.title} className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-pink-100 mb-2">
                <i className={`${item.icon} text-pink-500 text-sm`}></i>
              </div>
              <div className={`text-xs font-bold mb-1 ${headingColor}`}>{item.title}</div>
              <div className={`text-xs leading-relaxed ${subColor}`}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Banners section */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-megaphone-line text-pink-400 mr-1.5"></i>
            Announcement Banners ({banners.length})
          </h3>
          <button
            onClick={() => setEditing(newBanner())}
            className="text-xs font-bold text-pink-500 hover:text-pink-700 cursor-pointer whitespace-nowrap"
          >
            + New Banner
          </button>
        </div>
        {banners.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-50 mx-auto mb-3">
              <i className="ri-megaphone-line text-pink-400 text-xl"></i>
            </div>
            <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No banners yet</p>
            <p className={`text-xs mb-4 ${subColor}`}>Create an announcement banner to show at the top of every page.</p>
            <button
              onClick={() => setEditing(newBanner())}
              className="bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              Create Banner
            </button>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
            {banners.map(renderRow)}
          </div>
        )}
      </div>

      {/* Popups section */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-window-line text-pink-400 mr-1.5"></i>
            Popup Modals ({popups.length})
          </h3>
          <button
            onClick={() => setEditing(newPopup())}
            className="text-xs font-bold text-pink-500 hover:text-pink-700 cursor-pointer whitespace-nowrap"
          >
            + New Popup
          </button>
        </div>
        {popups.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-50 mx-auto mb-3">
              <i className="ri-window-line text-pink-400 text-xl"></i>
            </div>
            <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No popups yet</p>
            <p className={`text-xs mb-4 ${subColor}`}>Create a popup to engage visitors with announcements or offers.</p>
            <button
              onClick={() => setEditing(newPopup())}
              className="bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-5 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              Create Popup
            </button>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
            {popups.map(renderRow)}
          </div>
        )}
      </div>

      {draftItems.length > 0 && (
        <div className={`rounded-2xl border p-4 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-amber-50 border-amber-200"}`}>
          <p className={`text-xs ${isDark ? "text-gray-400" : "text-amber-700"}`}>
            <i className="ri-information-line mr-1"></i>
            <strong>Note:</strong> Banners appear above the navbar on every page. Popups appear based on their trigger setting. Visitors who dismiss a &quot;show once&quot; popup won&apos;t see it again on the same device.
          </p>
        </div>
      )}
    </div>
  );
}
